import { memo, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ArrowUp, ArrowDown, Trash2, Copy, Plus, ZoomIn, ZoomOut } from "lucide-react";
import {
  CELL_TYPE_LABELS, STATIC_TYPES,
  addColumn, deleteColumn, addRow, deleteRow, duplicateRow, moveRow, setCell, cellStarts,
} from "./gridModel";

const ROW_TYPE_STYLE = {
  header: "font-semibold",
  section: "font-semibold",
  instruction: "italic text-slate-500",
  spacer: "",
  signature: "",
  continuation: "text-slate-500",
};

const SIG_PLACEHOLDER = {
  contractor_signature: "[ Contractor Signature ]",
  client_signature: "[ Client Signature ]",
  superintendent_signature: "[ Superintendent Signature ]",
};

const CellContent = ({ cell, pointType }) => {
  const t = cell.type || "static_text";
  if (STATIC_TYPES.has(t)) {
    return (
      <span className={`whitespace-pre-wrap break-words ${t === "heading" ? "font-bold" : ""}`}>
        {cell.value || ""}
      </span>
    );
  }
  if (SIG_PLACEHOLDER[t]) {
    return <span className="block text-center text-[10px] font-medium text-slate-400">{SIG_PLACEHOLDER[t]}</span>;
  }
  if (t === "inspection_point") {
    return <span className="block text-center text-xs font-bold text-slate-700">{pointType || "—"}</span>;
  }
  return (
    <span className="inline-flex max-w-full items-center gap-1 rounded border border-dashed border-slate-300 bg-slate-50/70 px-1 py-0.5 text-[10px] leading-tight text-slate-500">
      {CELL_TYPE_LABELS[t] || t}
      {cell.required && <span className="text-rose-600">*</span>}
    </span>
  );
};

const GridRow = memo(function GridRow({ row, rowIdx, editable, selRowId, selCellId, onSelectCell, onCommitCell }) {
  const isSpacer = row.row_type === "spacer";
  const pt = row.rules?.point_type;
  return (
    <tr data-testid={`grid-row-${rowIdx}`}>
      {row.cells.map((cell, j) => {
        if (cell.merged_above) return null;
        const selectedCell = selCellId === cell.id;
        const style = {
          background: cell.shading || row.shading || (row.row_type === "header" ? "#F1F5F9" : undefined),
          textAlign: cell.align || undefined,
          verticalAlign: cell.valign || "top",
          fontSize: cell.font_size ? `${cell.font_size}px` : undefined,
          fontWeight: cell.bold ? 700 : undefined,
          minHeight: row.min_height ? `${row.min_height}px` : undefined,
          border: cell.borders === "none" ? "1px dashed #E2E8F0" : `${cell.border_width || 1}px solid #94A3B8`,
        };
        return (
          <td
            key={cell.id}
            colSpan={cell.column_span > 1 ? cell.column_span : undefined}
            rowSpan={cell.row_span > 1 ? cell.row_span : undefined}
            style={style}
            onClick={(e) => { e.stopPropagation(); onSelectCell?.(rowIdx, j, cell, row); }}
            className={`relative px-1.5 text-xs text-slate-800 ${isSpacer ? "py-0.5" : "py-1"} ${ROW_TYPE_STYLE[row.row_type] || ""} cursor-pointer ${selectedCell ? "outline outline-2 -outline-offset-2 outline-teal-600" : selRowId === row.id ? "outline outline-1 -outline-offset-1 outline-teal-300" : ""}`}
            data-testid={`grid-cell-${rowIdx}-${j}`}
          >
            {j === 0 && pt && (
              <span className="absolute -left-0.5 top-0 rounded-br bg-amber-500 px-1 text-[9px] font-bold text-white" title="Inspection point">{pt}</span>
            )}
            {editable && selectedCell && STATIC_TYPES.has(cell.type || "static_text") ? (
              <InlineEdit value={cell.value || ""} onCommit={(v) => onCommitCell(rowIdx, j, { value: v })} />
            ) : (
              <CellContent cell={cell} pointType={pt} />
            )}
          </td>
        );
      })}
    </tr>
  );
}, (a, b) => a.row === b.row && a.editable === b.editable && a.rowIdx === b.rowIdx
  && (a.selRowId === a.row.id) === (b.selRowId === b.row.id)
  && (a.selRowId !== a.row.id || a.selCellId === b.selCellId));

function InlineEdit({ value, onCommit }) {
  const [v, setV] = useState(value);
  return (
    <textarea
      autoFocus
      rows={Math.min(6, Math.max(1, Math.ceil((v || "").length / 25)))}
      value={v}
      onChange={(e) => setV(e.target.value)}
      onBlur={() => { if (v !== value) onCommit(v); }}
      onClick={(e) => e.stopPropagation()}
      className="w-full resize-none border-0 bg-transparent p-0 text-xs outline-none"
      data-testid="grid-cell-inline-edit"
    />
  );
}

export default function ITPGridEditor({ config, onChange, editable, sel, onSel, compact = false }) {
  const [zoom, setZoom] = useState(1);
  const [dragWidths, setDragWidths] = useState(null);
  const dragRef = useRef(null);
  const tableRef = useRef(null);

  const widths = dragWidths || config.columns.map((c) => c.width || 1);
  const total = widths.reduce((s, w) => s + w, 0) || 1;
  const pct = widths.map((w) => (w / total) * 100);

  const apply = (result) => {
    if (result?.error) { toast.error(result.error); return; }
    onChange(result);
  };

  const selRow = sel?.rowIdx != null ? config.rows[sel.rowIdx] : null;
  const selCell = selRow && sel.cellIdx != null ? selRow.cells[sel.cellIdx] : null;
  const selColIdx = sel?.colIdx != null ? sel.colIdx
    : selRow && selCell ? cellStarts(selRow)[sel.cellIdx] : null;

  const onSelectCell = (rowIdx, cellIdx) => { onSel?.({ rowIdx, cellIdx }); };
  const onCommitCell = (rowIdx, cellIdx, patch) => onChange(setCell(config, rowIdx, cellIdx, patch));

  const startResize = (e, colIdx) => {
    if (!editable) return;
    e.preventDefault(); e.stopPropagation();
    const tw = tableRef.current?.offsetWidth || 800;
    dragRef.current = { colIdx, startX: e.clientX, tw, base: config.columns.map((c) => c.width || 1) };
    const move = (ev) => {
      const d = dragRef.current;
      const deltaUnits = ((ev.clientX - d.startX) / d.tw) * d.base.reduce((s, w) => s + w, 0);
      const next = [...d.base];
      const na = Math.max(1.5, d.base[d.colIdx] + deltaUnits);
      const nb = Math.max(1.5, d.base[d.colIdx + 1] - (na - d.base[d.colIdx]));
      next[d.colIdx] = na; next[d.colIdx + 1] = nb;
      setDragWidths(next);
    };
    const up = (ev) => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
      const d = dragRef.current;
      const deltaUnits = ((ev.clientX - d.startX) / d.tw) * d.base.reduce((s, w) => s + w, 0);
      setDragWidths(null);
      if (Math.abs(deltaUnits) > 0.1) {
        const na = Math.max(1.5, d.base[d.colIdx] + deltaUnits);
        const nb = Math.max(1.5, d.base[d.colIdx + 1] - (na - d.base[d.colIdx]));
        const columns = config.columns.map((c, i) =>
          i === d.colIdx ? { ...c, width: Math.round(na * 100) / 100 }
            : i === d.colIdx + 1 ? { ...c, width: Math.round(nb * 100) / 100 } : c);
        onChange({ ...config, columns });
      }
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
  };

  return (
    <div data-testid="itp-grid-editor">
      {editable && !compact && (
        <div className="mb-1.5 flex flex-wrap items-center gap-1" data-testid="grid-toolbar">
          <ToolBtn testid="grid-add-row-below" title="Add a row" onClick={() => apply(addRow(config, selRow ? sel.rowIdx + 1 : config.rows.length, "item"))}><Plus className="mr-0.5 size-3"/>Row</ToolBtn>
          <ToolBtn testid="grid-dup-row" title="Duplicate the selected row" disabled={!selRow} onClick={() => apply(duplicateRow(config, sel.rowIdx))}><Copy className="mr-0.5 size-3"/>Duplicate</ToolBtn>
          <ToolBtn testid="grid-move-row-up" title="Move row up" disabled={!selRow || sel.rowIdx === 0} onClick={() => { apply(moveRow(config, sel.rowIdx, -1)); onSel({ ...sel, rowIdx: sel.rowIdx - 1 }); }}><ArrowUp className="size-3"/></ToolBtn>
          <ToolBtn testid="grid-move-row-down" title="Move row down" disabled={!selRow || sel.rowIdx === config.rows.length - 1} onClick={() => { apply(moveRow(config, sel.rowIdx, 1)); onSel({ ...sel, rowIdx: sel.rowIdx + 1 }); }}><ArrowDown className="size-3"/></ToolBtn>
          <ToolBtn testid="grid-del-row" title="Delete the selected row" disabled={!selRow} danger onClick={() => { apply(deleteRow(config, sel.rowIdx)); onSel(null); }}><Trash2 className="mr-0.5 size-3"/>Row</ToolBtn>
          <span className="mx-1 h-4 w-px bg-slate-200"/>
          <ToolBtn testid="grid-add-col-right" title="Add a column" onClick={() => apply(addColumn(config, selColIdx != null ? selColIdx + (selCell?.column_span || 1) : config.columns.length))}><Plus className="mr-0.5 size-3"/>Column</ToolBtn>
          <ToolBtn testid="grid-del-col" title="Delete the selected column" disabled={selColIdx == null} danger onClick={() => { apply(deleteColumn(config, selColIdx)); onSel(null); }}><Trash2 className="mr-0.5 size-3"/>Column</ToolBtn>
          <span className="ml-auto flex items-center gap-0.5">
            <ToolBtn testid="grid-zoom-out" title="Zoom out" onClick={() => setZoom((z) => Math.max(0.5, Math.round((z - 0.1) * 10) / 10))}><ZoomOut className="size-3"/></ToolBtn>
            <span className="w-9 text-center text-[10px] text-slate-500">{Math.round(zoom * 100)}%</span>
            <ToolBtn testid="grid-zoom-in" title="Zoom in" onClick={() => setZoom((z) => Math.min(1.5, Math.round((z + 0.1) * 10) / 10))}><ZoomIn className="size-3"/></ToolBtn>
          </span>
        </div>
      )}
      <div className="overflow-x-auto" style={{ zoom }}>
        <table ref={tableRef} className="w-full border-collapse" style={{ tableLayout: "fixed", minWidth: config.columns.length > 6 ? 760 : 320 }}>
          <colgroup>{config.columns.map((c, i) => <col key={c.id} style={{ width: `${pct[i]}%` }}/>)}</colgroup>
          {editable && !compact && (
            <thead>
              <tr>
                {config.columns.map((c, i) => (
                  <th
                    key={c.id}
                    onClick={() => onSel({ colIdx: i })}
                    className={`relative select-none border border-slate-300 bg-slate-100 px-1 py-0.5 text-[10px] font-medium text-slate-500 ${sel?.colIdx === i ? "bg-teal-100 text-teal-800" : ""}`}
                    title="Click to edit this column"
                    data-testid={`grid-col-head-${i}`}
                  >
                    <span className="block truncate">{c.label || `Column ${i + 1}`}</span>
                    {i < config.columns.length - 1 && (
                      <span
                        onPointerDown={(e) => startResize(e, i)}
                        className="absolute -right-1 top-0 z-10 h-full w-2 cursor-col-resize"
                        data-testid={`grid-col-resize-${i}`}
                      />
                    )}
                  </th>
                ))}
              </tr>
            </thead>
          )}
          <tbody>
            {config.rows.map((row, i) => (
              <GridRow
                key={row.id}
                row={row}
                rowIdx={i}
                editable={!!editable}
                selRowId={selRow?.id || null}
                selCellId={selCell?.id || null}
                onSelectCell={onSelectCell}
                onCommitCell={onCommitCell}
              />
            ))}
          </tbody>
        </table>
      </div>
      {editable && !compact && (
        <div className="mt-1 flex items-center gap-2 text-[10px] text-slate-400">
          <span>{config.rows.length} rows × {config.columns.length} columns (max 12)</span>
          <button className="text-teal-700 underline-offset-2 hover:underline" onClick={() => apply(addRow(config, config.rows.length, "item"))} data-testid="grid-quick-add-row">+ Add row</button>
        </div>
      )}
    </div>
  );
}

const ToolBtn = ({ children, title, onClick, disabled, danger, testid }) => (
  <Button
    variant="outline" size="sm" title={title} disabled={disabled}
    onClick={(e) => { e.stopPropagation(); onClick(); }}
    className={`h-6 gap-0 px-1.5 text-[11px] ${danger ? "text-rose-600" : ""}`}
    data-testid={testid}
  >
    {children}
  </Button>
);
